import {
  mentionRangeSignalToContent,
  mentionSignalToContent,
} from "@/lib/mentions";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import ListItem from "@tiptap/extension-list-item";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import { Markdown } from "tiptap-markdown";
import {
  useRangeMentionSignal,
  useSectionMentionSignal,
} from "../../hooks/use-mention-signal";
import { Command } from "../Command/CommandExtension";

interface TipTapTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  enableCommand?: boolean;
}

export const TipTapTextarea: React.FC<TipTapTextareaProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder = "How can I help",
  disabled = false,
  enableCommand = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMentionListOpen, setIsMentionListOpen] = useState(false);
  const { listen } = useSectionMentionSignal();
  const { listen: listenRange } = useRangeMentionSignal();
  const initializedRef = useRef(false);
  const shouldRefocus = useRef(false);

  listen((value) => {
    editor?.commands.insertContent(mentionSignalToContent(value));
  });

  listenRange((signal) => {
    editor?.commands.insertContent(mentionRangeSignalToContent(signal));
  });

  const extensions = [
    Document,
    Paragraph,
    Text,
    BulletList,
    ListItem,
    HardBreak,
    Markdown,
    Placeholder.configure({
      placeholder,
    }),
  ];

  if (enableCommand) {
    extensions.push(Command.configure({ openChange: setIsMentionListOpen }));
  }

  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    content: value,
    // This won't update when `disabled` props changes,
    // We handle that in a useEffect below
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      onChange(text);
    },
    editorProps: {
      handleKeyDown: (_, event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          // Don't submit if MentionList is open - let it handle Enter
          if (isMentionListOpen) {
            return false;
          }
          event.preventDefault();
          onSubmit();
          return true;
        }
        // Let Shift+Enter create a line break (handled by HardBreak extension)
        return false;
      },
      attributes: {
        class:
          "tiptap mt-1.5 flex-grow resize-none overflow-hidden overflow-y-auto bg-transparent text-sm outline-none placeholder:text-gray-500",
        style: "width: 100%",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    // Focus on initialization
    if (!initializedRef.current) {
      editor.commands.focus();
      initializedRef.current = true;
    }

    if (disabled === editor.isEditable) {
      editor.setEditable(!disabled, false);
      if (disabled) {
        shouldRefocus.current = true;
      }
    }

    // Focus again if it becomes enabled
    if (!disabled && shouldRefocus.current) {
      editor.commands.focus("end");
      shouldRefocus.current = false;
    }
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    if (editor.getText() !== value) {
      editor.commands.setContent(value, false);
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <div ref={containerRef} className="flex flex-1">
      <EditorContent
        data-testid="tiptap-textarea"
        editor={editor}
        className="max-h-50 flex-1 cursor-text overflow-y-auto"
        onClick={() => {
          editor.commands.focus();
        }}
      />
    </div>
  );
};
